import React, { useState, useEffect } from 'react';
import { X, User, Mail, Building2, Shield, Clock, Wifi, WifiOff, Download, CheckSquare, Square, Lock, Key, Eye, EyeOff, Plus, Star, Trash2, Loader2, FolderOpen, Navigation, Users, Briefcase, Monitor, Smartphone, FileText, Bot, ChevronDown, ChevronUp, Bell, BellOff, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { cacheDevice, cacheDeviceTypes } from '../utils/offlineDB';
import { SLOT_OPTIONS, updateSlotConfig } from '../BottomNav';
import { API_URL } from '../config/api';

// Consent type labels and content
const CONSENT_DATA = {
  terms_of_service: {
    name: 'Käyttöehdot',
    icon: FileText,
    content: `1. SOPIMUS JA SOVELTAMISALA

Nämä käyttöehdot koskevat OneTrack-palvelun käyttöä. Palvelu sisältää selainpohjaisen työkalupaketin ja PWA-sovelluksen.

2. TILIT JA PÄÄSYNHALLINTA

Palvelun käyttö edellyttää käyttäjätiliä. Asiakas vastaa siitä, että käyttäjätiedot ovat oikein ja ajan tasalla.

3. ASIAKKAAN VASTUUT

Asiakas vastaa palveluun syötettyjen tietojen lainmukaisuudesta, oikeellisuudesta ja ajantasaisuudesta.

4. PALVELUN SAATAVUUS

Palvelu tarjotaan "sellaisena kuin se on". Toimittaja voi kehittää ja muuttaa ominaisuuksia.

5. DATA JA OIKEUDET

Asiakas omistaa palveluun syöttämänsä tiedot. Toimittaja ei käytä asiakasdataa muihin tarkoituksiin kuin palvelun tuottamiseen.

6. TIETOTURVA

Toimittaja toteuttaa kohtuulliset tekniset ja organisatoriset toimenpiteet palvelun suojaamiseksi.

7. VASTUUNRAJOITUS

Palvelu on operatiivisen toiminnan tukijärjestelmä. Toimittaja ei vastaa välillisistä vahingoista.`
  },
  privacy_notice: {
    name: 'Tietosuojaseloste',
    icon: Shield,
    content: `TIETOSUOJASELOSTE

1. REKISTERINPITÄJÄ
OneTrack-palvelun toimittaja.

2. KÄSITTELYN TARKOITUKSET
• Käyttäjätilien luominen ja hallinta
• Asiakassuhteen hoito
• Asiakastuki ja virhetilanteiden selvittäminen
• Tietoturva ja lokitus
• Palvelun kehittäminen

3. KÄSITELTÄVÄT TIEDOT
• Tunnistetiedot: nimi, sähköposti
• Organisaatiotiedot: työnimike, osasto, rooli
• Tekniset tiedot: istunto- ja lokitiedot

4. REKISTERÖIDYN OIKEUDET
• Oikeus saada pääsy tietoihin
• Oikeus oikaista ja poistaa tiedot
• Oikeus rajoittaa ja vastustaa käsittelyä
• Oikeus siirtää tiedot
• Oikeus tehdä valitus tietosuojavaltuutetulle

5. SÄILYTYSAJAT
• Käyttäjätilit: sopimuksen ajan + 12 kk
• Lokit: 12 kuukautta
• Työmaapäiväkirja: 10 vuotta (YSE 1998)`
  },
  ai_usage: {
    name: 'AI-toimintojen käyttö',
    icon: Bot,
    content: `AI-LISÄEHDOT

OneTrack sisältää tekoälyavustimia, jotka lähettävät tietoja kolmannen osapuolen palveluihin (Anthropic Claude, OpenAI).

LÄHETETTÄVÄT TIEDOT:
• QMS: poikkeamien kuvaukset, dokumenttien nimet
• Päiväkirja: projektin tiedot, yhteenvedot
• Laitteet: laitetiedot, huoltohistoria
• Korttien tunnistus: korttikuvat

ÄLÄ SYÖTÄ AI:LLE:
• Henkilötunnuksia tai salasanoja
• Maksukortti- tai pankkitietoja
• Terveystietoja tai arkaluonteisia tietoja

TÄRKEÄÄ:
• AI tuottaa ehdotuksia, ei päätöksiä
• AI voi tehdä virheitä
• Tarkista kriittiset tiedot aina itse
• AI-keskusteluja ei tallenneta pysyvästi
• Palveluntarjoaja voi säilyttää dataa max 30 päivää`
  },
  marketing: {
    name: 'Markkinointiviestintä',
    icon: Mail,
    content: `MARKKINOINTIVIESTINTÄ

Haluan vastaanottaa tuoteuutisia ja päivityksiä sähköpostitse.

Tämä on valinnainen suostumus. Voit peruuttaa sen milloin tahansa.`
  }
};

// Slot descriptions for UI
const SLOT_DESCRIPTIONS = {
  qms: 'Poikkeamat ja laadunhallinta',
  tasks: 'Projektit ja tehtävät',
  training: 'Kortit ja koulutukset',
  repairs: 'Korjaustilaukset',
  hourReporting: 'Työaikakirjaukset',
  reports: 'Analytiikka ja tilastot',
  crm: 'Asiakashallinta ja myynti'
};

// Permission requirements for each slot option
const SLOT_PERMISSIONS = {
  repairs: 'repairs.view',
  hourReporting: 'hours.view',
  reports: 'reports.view'
};

// Feature flag requirements for each slot option
const SLOT_FEATURES = {
  qms: 'feature_qms',
  hourReporting: 'feature_hour_tracking',
  diary: 'feature_diary',
  crm: 'feature_crm'
};

function ProfileModal({ onClose, hasPermission, isAdmin, isSuperAdmin: propIsSuperAdmin }) {
  const { user, tenant, isSuperAdmin: contextIsSuperAdmin } = useAuth();
  const { language, changeLanguage, t } = useTranslation();
  // Use props if provided, otherwise fall back to context
  const effectiveIsSuperAdmin = propIsSuperAdmin !== undefined ? propIsSuperAdmin : contextIsSuperAdmin;
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isPrecaching, setIsPrecaching] = useState(false);
  const [precacheProgress, setPrecacheProgress] = useState({ current: 0, total: 0 });

  // My Projects states (user's department assignments)
  const [myDepartments, setMyDepartments] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [loadingMyDepts, setLoadingMyDepts] = useState(false);
  const [addingDept, setAddingDept] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');

  // Bottom nav slot configuration
  const [slotConfig, setSlotConfig] = useState(() => ({
    slot1: localStorage.getItem('bottomNavSlot1') || 'qms',
    slot2: localStorage.getItem('bottomNavSlot2') || 'tasks',
    slot3: localStorage.getItem('bottomNavSlot3') || 'training'
  }));

  // Handler to update a slot
  const handleSlotChange = (slotKey, value) => {
    setSlotConfig(prev => ({ ...prev, [slotKey]: value }));
    updateSlotConfig(slotKey, value);
  };

  // Job role and subordinates
  const [jobRole, setJobRole] = useState(null);
  const [subordinates, setSubordinates] = useState([]);

  // Consent states
  const [userConsents, setUserConsents] = useState([]);
  const [consentsExpanded, setConsentsExpanded] = useState(false);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [viewingConsent, setViewingConsent] = useState(null); // consent type being viewed

  // Push notification states
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    push_enabled: true,
    notify_new_task: true,
    notify_task_completed: true,
    notify_fault_report: true,
    notify_qms_approval: true,
    notify_message: true,
    notify_device_alert: true,
    notify_geofence: true,
    notify_tamper: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00'
  });

  // Load offline setting and departments
  useEffect(() => {
    const saved = localStorage.getItem('offlineEnabled');
    setOfflineEnabled(saved === 'true');

    const savedDepts = localStorage.getItem('selectedDepartments');
    if (savedDepts) {
      setSelectedDepartments(JSON.parse(savedDepts));
    }

    loadDepartments();
    loadMyDepartments();
    loadProfileData();
    loadUserConsents();
  }, []);

  // Load user consents
  const loadUserConsents = async () => {
    setLoadingConsents(true);
    try {
      const response = await fetch(`${API_URL}/consents/check.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.valid) {
        setUserConsents(data.valid);
      }
    } catch (error) {
      console.error('Failed to load consents:', error);
    } finally {
      setLoadingConsents(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await fetch(`${API_URL}/departments/list.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setDepartments(data.departments || []);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  // Load user's own department assignments
  const loadMyDepartments = async () => {
    setLoadingMyDepts(true);
    try {
      const response = await fetch(`${API_URL}/users/my-departments.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMyDepartments(data.departments || []);
        setAvailableDepartments(data.available_departments || []);
        setIsAdminUser(data.is_admin || false);
      }
    } catch (error) {
      console.error('Failed to load my departments:', error);
    } finally {
      setLoadingMyDepts(false);
    }
  };

  // Load job role and subordinates
  const loadProfileData = async () => {
    try {
      const response = await fetch(`${API_URL}/users/my-profile-data.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setJobRole(data.job_role);
        setSubordinates(data.subordinates || []);
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    }
  };

  // Check push notification support
  useEffect(() => {
    const checkPushSupport = async () => {
      // Check if push notifications are supported
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setPushSupported(supported);

      if (supported) {
        setPushPermission(Notification.permission);

        // Check if already subscribed - browser is the source of truth
        let browserSubscribed = false;
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          browserSubscribed = !!subscription;
          setPushSubscribed(browserSubscribed);
        } catch (err) {
          console.error('Error checking push subscription:', err);
        }

        // Load user notification settings (but don't override browser subscription state)
        try {
          const response = await fetch(`${API_URL}/notifications/user-settings.php`, {
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            setNotificationSettings(data.settings);
            // Keep browserSubscribed as source of truth, don't use data.subscribed
          }
        } catch (err) {
          console.error('Error loading notification settings:', err);
        }
      }
    };

    checkPushSupport();
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    setPushLoading(true);
    setPushError(null);

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission !== 'granted') {
        setPushError('Ilmoituslupa evätty. Salli ilmoitukset selaimen asetuksista.');
        setPushLoading(false);
        return;
      }

      // Get VAPID public key
      const vapidResponse = await fetch(`${API_URL}/notifications/vapid-key.php`, {
        credentials: 'include'
      });
      const vapidData = await vapidResponse.json();

      if (!vapidData.success || !vapidData.publicKey) {
        setPushError('Push-ilmoitukset eivät ole käytössä tässä organisaatiossa.');
        setPushLoading(false);
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
      });

      // Send subscription to server
      const response = await fetch(`${API_URL}/notifications/subscribe.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });

      const data = await response.json();

      if (data.success) {
        setPushSubscribed(true);
      } else {
        setPushError(data.message || 'Tilaus epäonnistui');
      }
    } catch (err) {
      console.error('Push subscription error:', err);
      setPushError('Virhe ilmoitusten käyttöönotossa');
    } finally {
      setPushLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    setPushLoading(true);
    setPushError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Notify server
        await fetch(`${API_URL}/notifications/unsubscribe.php`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
      }

      setPushSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe error:', err);
      setPushError('Virhe ilmoitusten poistossa');
    } finally {
      setPushLoading(false);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async (newSettings) => {
    try {
      const response = await fetch(`${API_URL}/notifications/user-settings.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      const data = await response.json();
      if (data.success) {
        setNotificationSettings(newSettings);
      }
    } catch (err) {
      console.error('Error saving notification settings:', err);
    }
  };

  // Add department to user's profile
  const handleAddDepartment = async (deptId) => {
    setAddingDept(true);
    try {
      const response = await fetch(`${API_URL}/users/my-departments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ department_id: deptId })
      });
      const data = await response.json();
      if (data.success) {
        loadMyDepartments(); // Reload to get updated list
      } else {
        alert(data.message || 'Lisäys epäonnistui');
      }
    } catch (error) {
      console.error('Failed to add department:', error);
      alert('Verkkovirhe');
    } finally {
      setAddingDept(false);
    }
  };

  // Remove department from user's profile
  const handleRemoveDepartment = async (deptId) => {
    try {
      const response = await fetch(`${API_URL}/users/my-departments.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ department_id: deptId })
      });
      const data = await response.json();
      if (data.success) {
        setMyDepartments(prev => prev.filter(d => d.department_id !== deptId));
      } else {
        alert(data.message || 'Poisto epäonnistui');
      }
    } catch (error) {
      console.error('Failed to remove department:', error);
      alert('Verkkovirhe');
    }
  };

  // Save offline setting to localStorage
  const handleOfflineToggle = (enabled) => {
    setOfflineEnabled(enabled);
    localStorage.setItem('offlineEnabled', enabled.toString());

    if (enabled) {
      console.log('✅ Offline mode enabled - devices will be cached');
    } else {
      console.log('⚠️ Offline mode disabled - devices will not be cached');
    }
  };

  // Toggle department selection
  const toggleDepartment = (deptId) => {
    setSelectedDepartments(prev => {
      const newSelection = prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId];

      localStorage.setItem('selectedDepartments', JSON.stringify(newSelection));
      return newSelection;
    });
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess('');

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordChangeError('Kaikki kentät ovat pakollisia');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordChangeError('Uuden salasanan tulee olla vähintään 8 merkkiä pitkä');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordChangeError('Salasanat eivät täsmää');
      return;
    }

    setPasswordChangeLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/change-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setPasswordChangeSuccess(data.message);
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Hide password change section after 2 seconds
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordChangeSuccess('');
        }, 2000);
      } else {
        setPasswordChangeError(data.message || 'Salasanan vaihto epäonnistui');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordChangeError('Verkkovirhe. Yritä uudelleen.');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Pre-cache devices from selected departments
  const preCacheDevices = async () => {
    if (selectedDepartments.length === 0) {
      alert('Valitse vähintään yksi osasto');
      return;
    }

    setIsPrecaching(true);
    setPrecacheProgress({ current: 0, total: 0 });

    try {
      // First, cache device types with fields for offline use
      try {
        const typesResponse = await fetch(`${API_URL}/device-types/get-types.php?include_fields=1`, {
          credentials: 'include'
        });
        const typesData = await typesResponse.json();
        if (typesData.success) {
          await cacheDeviceTypes(typesData.types);
          console.log('💾 Device types with fields cached for offline use');
        }
      } catch (typeError) {
        console.error('Failed to cache device types:', typeError);
      }

      // Then, get all devices
      const response = await fetch(`${API_URL}/devices/list.php`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        // Filter devices by selected departments
        const devicesToCache = data.devices.filter(device =>
          selectedDepartments.includes(device.department_id)
        );

        console.log(`📦 Pre-caching ${devicesToCache.length} devices from ${selectedDepartments.length} departments...`);
        setPrecacheProgress({ current: 0, total: devicesToCache.length });

        let cached = 0;
        let failed = 0;

        // Cache each device with full details
        for (const device of devicesToCache) {
          try {
            const detailResponse = await fetch(
              `${API_URL}/devices/get.php?id=${device.id}&check_user_trainings=1`,
              { credentials: 'include' }
            );
            const detailData = await detailResponse.json();

            if (detailData.success) {
              const deviceData = {
                ...detailData.device,
                history: detailData.history,
                required_trainings: detailData.required_trainings,
                training_access: detailData.training_access,
                active_faults: detailData.active_faults,
                pending_faults: detailData.pending_faults,
                upcoming_repairs: detailData.upcoming_repairs
              };

              await cacheDevice(deviceData);
              cached++;
              setPrecacheProgress({ current: cached + failed, total: devicesToCache.length });
            } else {
              failed++;
            }
          } catch (error) {
            console.error(`Failed to cache device ${device.id}:`, error);
            failed++;
          }
        }

        console.log(`✅ Pre-caching complete: ${cached} cached, ${failed} failed`);
        alert(`Välimuistiin tallennettu ${cached} laitetta!\n${failed > 0 ? `${failed} epäonnistui.` : ''}`);
      }
    } catch (error) {
      console.error('Pre-caching failed:', error);
      alert('Välimuistiin tallennus epäonnistui');
    } finally {
      setIsPrecaching(false);
      setPrecacheProgress({ current: 0, total: 0 });
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          animation: 'fadeIn 0.3s ease-out'
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#1e293b',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 9999,
        animation: 'slideIn 0.3s ease-out',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #334155',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: '#1e293b',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <User size={24} />
            Profiili
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              background: '#334155',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#334155'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* User Info Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '1rem'
            }}>
              Käyttäjätiedot
            </h3>

            {/* Name */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#94a3b8',
                fontSize: '13px',
                marginBottom: '0.25rem'
              }}>
                <User size={16} />
                Nimi
              </div>
              <div style={{
                color: 'white',
                fontSize: '15px',
                fontWeight: '500'
              }}>
                {user?.first_name} {user?.last_name}
              </div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#94a3b8',
                fontSize: '13px',
                marginBottom: '0.25rem'
              }}>
                <Mail size={16} />
                Sähköposti
              </div>
              <div style={{
                color: 'white',
                fontSize: '15px',
                fontWeight: '500'
              }}>
                {user?.email}
              </div>
            </div>

            {/* Tenant */}
            {tenant && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#94a3b8',
                  fontSize: '13px',
                  marginBottom: '0.25rem'
                }}>
                  <Building2 size={16} />
                  Yritys
                </div>
                <div style={{
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '500'
                }}>
                  {tenant.name}
                </div>
              </div>
            )}

            {/* Role */}
            <div style={{ marginBottom: 0 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#94a3b8',
                fontSize: '13px',
                marginBottom: '0.25rem'
              }}>
                <Shield size={16} />
                Rooli
              </div>
              <div style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '6px',
                background: effectiveIsSuperAdmin ? '#FFEB3B' : tenant?.role === 'owner' || tenant?.role === 'admin' ? '#10b981' : '#334155',
                color: effectiveIsSuperAdmin ? '#0f172a' : 'white',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {effectiveIsSuperAdmin ? 'Superadmin' :
                 tenant?.role === 'owner' ? 'Omistaja' :
                 tenant?.role === 'admin' ? 'Admin' :
                 tenant?.role === 'member' ? 'Jäsen' :
                 tenant?.role || 'Käyttäjä'}
              </div>
            </div>

            {/* Job Role */}
            {jobRole && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#94a3b8',
                  fontSize: '13px',
                  marginBottom: '0.25rem'
                }}>
                  <Briefcase size={16} />
                  Työrooli
                </div>
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '6px',
                  background: jobRole.hierarchy_level === 'hallinto' ? '#fef3c7' :
                             jobRole.hierarchy_level === 'tyomaapaallikko' ? '#dbeafe' :
                             jobRole.hierarchy_level === 'tyonjohtaja' ? '#dcfce7' : '#f3f4f6',
                  color: jobRole.hierarchy_level === 'hallinto' ? '#92400e' :
                         jobRole.hierarchy_level === 'tyomaapaallikko' ? '#1e40af' :
                         jobRole.hierarchy_level === 'tyonjohtaja' ? '#166534' : '#4b5563',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  {jobRole.name}
                </div>
              </div>
            )}
          </div>

          {/* Language Settings Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Globe size={18} />
              {t('settings.language')}
            </h3>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => changeLanguage('fi')}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: language === 'fi' ? 'rgba(255, 235, 59, 0.15)' : '#1e293b',
                  border: language === 'fi' ? '2px solid #FFEB3B' : '1px solid #334155',
                  borderRadius: '8px',
                  color: language === 'fi' ? 'white' : '#94a3b8',
                  fontSize: '14px',
                  fontWeight: language === 'fi' ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                🇫🇮 Suomi
              </button>
              <button
                onClick={() => changeLanguage('en')}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: language === 'en' ? 'rgba(255, 235, 59, 0.15)' : '#1e293b',
                  border: language === 'en' ? '2px solid #FFEB3B' : '1px solid #334155',
                  borderRadius: '8px',
                  color: language === 'en' ? 'white' : '#94a3b8',
                  fontSize: '14px',
                  fontWeight: language === 'en' ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                🇬🇧 English
              </button>
            </div>
          </div>

          {/* Subordinates Section - only show if user has subordinates */}
          {subordinates.length > 0 && (
            <div style={{
              background: '#0f172a',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Users size={18} />
                Tiimini ({subordinates.length})
              </h3>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                {subordinates.map(sub => (
                  <div
                    key={sub.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      background: '#1e293b',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#334155',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <User size={16} color="#94a3b8" />
                      </div>
                      <div>
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                          {sub.name}
                        </div>
                        {sub.job_role_name && (
                          <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                            {sub.job_role_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* My Projects Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FolderOpen size={18} />
              Omat projektit
            </h3>

            {loadingMyDepts ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Ladataan...
              </div>
            ) : (
              <>
                {/* Current departments */}
                {myDepartments.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem' }}>
                    {myDepartments.map(dept => (
                      <div
                        key={dept.department_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          backgroundColor: Number(dept.is_primary) === 1 ? '#374151' : '#1e293b',
                          border: Number(dept.is_primary) === 1 ? '1px solid #fbbf24' : '1px solid #374151',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: '#f3f4f6'
                        }}
                      >
                        {Number(dept.is_primary) === 1 && <Star size={12} color="#fbbf24" fill="#fbbf24" />}
                        <span>{dept.department_name}</span>
                        <button
                          onClick={() => handleRemoveDepartment(dept.department_id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px',
                            cursor: 'pointer',
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Poista projekti"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '1rem' }}>
                    Ei projekteja. Lisää projekti alla olevasta valikosta.
                  </div>
                )}

                {/* Add department dropdown */}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddDepartment(parseInt(e.target.value));
                    }
                  }}
                  disabled={addingDept}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#1e293b',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#f3f4f6',
                    fontSize: '14px',
                    cursor: addingDept ? 'not-allowed' : 'pointer',
                    opacity: addingDept ? 0.7 : 1
                  }}
                >
                  <option value="">{addingDept ? 'Lisätään...' : '+ Lisää projekti...'}</option>
                  {availableDepartments
                    .filter(dept => !myDepartments.some(md => md.department_id === dept.id))
                    .filter(dept => isAdminUser || dept.allow_self_assign !== false && dept.allow_self_assign !== 0 && dept.allow_self_assign !== '0')
                    .map(dept => (
                      <option key={dept.id} value={String(dept.id)}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </>
            )}
          </div>

          {/* Password Change Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: showPasswordChange ? '1rem' : 0
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                margin: 0
              }}>
                Salasana ja kirjautuminen
              </h3>

              <button
                onClick={() => {
                  setShowPasswordChange(!showPasswordChange);
                  setPasswordChangeError('');
                  setPasswordChangeSuccess('');
                  if (!showPasswordChange) {
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: showPasswordChange ? '#334155' : '#FFEB3B',
                  color: showPasswordChange ? 'white' : '#0f172a',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!showPasswordChange) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {showPasswordChange ? (
                  <>
                    <X size={14} />
                    Peruuta
                  </>
                ) : (
                  <>
                    <Key size={14} />
                    Vaihda salasana
                  </>
                )}
              </button>
            </div>

            {/* Password Change Form */}
            {showPasswordChange && (
              <form onSubmit={handlePasswordChange} style={{
                animation: 'fadeIn 0.3s ease-out'
              }}>
                {/* Current Password */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '0.5rem'
                  }}>
                    Nykyinen salasana
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Syötä nykyinen salasanasi"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        background: '#1e293b',
                        border: '2px solid #334155',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#94a3b8',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '0.5rem'
                  }}>
                    Uusi salasana
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Vähintään 8 merkkiä"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        background: '#1e293b',
                        border: '2px solid #334155',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#94a3b8',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '0.5rem'
                  }}>
                    Vahvista salasana
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Syötä salasana uudelleen"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        background: '#1e293b',
                        border: '2px solid #334155',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#94a3b8',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {passwordChangeError && (
                  <div style={{
                    padding: '0.75rem',
                    background: '#ef4444',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '13px',
                    marginBottom: '1rem'
                  }}>
                    {passwordChangeError}
                  </div>
                )}

                {/* Success Message */}
                {passwordChangeSuccess && (
                  <div style={{
                    padding: '0.75rem',
                    background: '#10b981',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '13px',
                    marginBottom: '1rem'
                  }}>
                    {passwordChangeSuccess}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={passwordChangeLoading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: passwordChangeLoading ? '#64748b' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: passwordChangeLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                >
                  {passwordChangeLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Vaihdetaan...
                    </>
                  ) : (
                    <>
                      <Lock size={16} />
                      Tallenna uusi salasana
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Push Notifications Section */}
          {pushSupported && (
            <div style={{
              background: '#0f172a',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Bell size={18} />
                Push-ilmoitukset
              </h3>

              {/* Subscription Toggle */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                padding: '1rem',
                background: '#1e293b',
                borderRadius: '8px',
                border: '1px solid #334155',
                marginBottom: pushSubscribed ? '1rem' : 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '500'
                  }}>
                    {pushSubscribed ? <Bell size={18} color="#10b981" /> : <BellOff size={18} color="#94a3b8" />}
                    Push-ilmoitukset {pushSubscribed ? 'käytössä' : 'pois päältä'}
                  </div>

                  <button
                    onClick={pushSubscribed ? unsubscribeFromPush : subscribeToPush}
                    disabled={pushLoading}
                    style={{
                      padding: '0.5rem 1rem',
                      background: pushSubscribed ? '#334155' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: pushLoading ? 'not-allowed' : 'pointer',
                      opacity: pushLoading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pushLoading ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ display: 'none' }} className="hide-mobile">
                          {pushSubscribed ? 'Poistetaan...' : 'Käyttöönotto...'}
                        </span>
                      </>
                    ) : pushSubscribed ? (
                      <>
                        <BellOff size={16} />
                        Poista
                      </>
                    ) : (
                      <>
                        <Bell size={16} />
                        Ota käyttöön
                      </>
                    )}
                  </button>
                </div>

                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px'
                }}>
                  {pushSubscribed
                    ? 'Saat ilmoituksia tehtävistä, hyväksynnöistä ja muista tapahtumista'
                    : 'Ota käyttöön saadaksesi ilmoituksia tällä laitteella'}
                </div>
              </div>

              {/* Error message */}
              {pushError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#ef444420',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  color: '#fca5a5',
                  fontSize: '13px',
                  marginTop: '0.75rem'
                }}>
                  {pushError}
                </div>
              )}

              {/* Notification type settings when subscribed */}
              {pushSubscribed && (
                <div style={{
                  background: '#1e293b',
                  borderRadius: '8px',
                  padding: '1rem',
                  animation: 'fadeIn 0.3s ease-out'
                }}>
                  <div style={{
                    color: '#94a3b8',
                    fontSize: '13px',
                    marginBottom: '0.75rem'
                  }}>
                    Valitse mistä tapahtumista haluat ilmoituksia:
                  </div>

                  {[
                    { key: 'notify_new_task', label: 'Uusi tehtävä', desc: 'Kun sinulle määrätään tehtävä' },
                    { key: 'notify_task_completed', label: 'Tehtävä valmis', desc: 'Kun tehtäväsi merkitään valmiiksi' },
                    { key: 'notify_qms_approval', label: 'Hyväksyntäpyynnöt', desc: 'Kun dokumentti odottaa hyväksyntääsi' },
                    { key: 'notify_fault_report', label: 'Vikailmoitukset', desc: 'Kun uusi vikailmoitus luodaan' },
                    { key: 'notify_device_alert', label: 'Laite-ilmoitukset', desc: 'Kalibrointi- ja huoltomuistutukset' },
                    { key: 'notify_geofence', label: 'Geofence-hälytykset', desc: 'Kun laite poistuu tai palaa alueelle' },
                    { key: 'notify_tamper', label: 'Irroitushälytykset', desc: 'Kun GPS-seurantalaite irrotetaan' }
                  ].map(item => (
                    <div
                      key={item.key}
                      onClick={() => {
                        const newSettings = {
                          ...notificationSettings,
                          [item.key]: !notificationSettings[item.key]
                        };
                        saveNotificationSettings(newSettings);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        background: '#0f172a',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div>
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                          {item.label}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                          {item.desc}
                        </div>
                      </div>
                      <div style={{
                        width: '44px',
                        height: '24px',
                        borderRadius: '12px',
                        background: notificationSettings[item.key] ? '#10b981' : '#475569',
                        position: 'relative',
                        transition: 'background 0.2s',
                        flexShrink: 0
                      }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '3px',
                          left: notificationSettings[item.key] ? '23px' : '3px',
                          transition: 'left 0.2s'
                        }} />
                      </div>
                    </div>
                  ))}

                  {/* Quiet hours */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#0f172a',
                    borderRadius: '6px'
                  }}>
                    <div
                      onClick={() => {
                        const newSettings = {
                          ...notificationSettings,
                          quiet_hours_enabled: !notificationSettings.quiet_hours_enabled
                        };
                        saveNotificationSettings(newSettings);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                    >
                      <div>
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                          Hiljaiset tunnit
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                          Ei ilmoituksia tiettyinä aikoina
                        </div>
                      </div>
                      <div style={{
                        width: '44px',
                        height: '24px',
                        borderRadius: '12px',
                        background: notificationSettings.quiet_hours_enabled ? '#10b981' : '#475569',
                        position: 'relative',
                        transition: 'background 0.2s',
                        flexShrink: 0
                      }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '3px',
                          left: notificationSettings.quiet_hours_enabled ? '23px' : '3px',
                          transition: 'left 0.2s'
                        }} />
                      </div>
                    </div>

                    {notificationSettings.quiet_hours_enabled && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid #334155'
                      }}>
                        <input
                          type="time"
                          value={notificationSettings.quiet_hours_start}
                          onChange={(e) => {
                            const newSettings = {
                              ...notificationSettings,
                              quiet_hours_start: e.target.value
                            };
                            saveNotificationSettings(newSettings);
                          }}
                          style={{
                            padding: '0.5rem',
                            background: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        />
                        <span style={{ color: '#64748b', fontSize: '13px' }}>—</span>
                        <input
                          type="time"
                          value={notificationSettings.quiet_hours_end}
                          onChange={(e) => {
                            const newSettings = {
                              ...notificationSettings,
                              quiet_hours_end: e.target.value
                            };
                            saveNotificationSettings(newSettings);
                          }}
                          style={{
                            padding: '0.5rem',
                            background: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* iOS instructions */}
              {!pushSubscribed && /iPhone|iPad/.test(navigator.userAgent) && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: '#1e3a5f',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  color: '#93c5fd',
                  fontSize: '13px'
                }}>
                  <strong>iOS-käyttäjille:</strong> Lisää sovellus ensin kotinäytölle (Safari → Jaa → Lisää Koti-valikkoon), jotta push-ilmoitukset toimivat.
                </div>
              )}
            </div>
          )}

          {/* Bottom Nav Customization Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Navigation size={18} />
              Alapalkki
            </h3>
            <p style={{
              color: '#94a3b8',
              fontSize: '13px',
              marginBottom: '1rem'
            }}>
              Mukauta alapalkin pikavalinnat. Paikka 1 näkyy sekä mobiilissa että desktopissa. Paikat 2 ja 3 näkyvät vain desktopissa.
            </p>

            {/* Slots */}
            {[
              { key: 'slot1', label: 'Paikka 1', hint: 'Mobiili + Desktop', icon: Smartphone },
              { key: 'slot2', label: 'Paikka 2', hint: 'Vain desktop', icon: Monitor },
              { key: 'slot3', label: 'Paikka 3', hint: 'Vain desktop', icon: Monitor }
            ].map(slot => {
              const SlotIcon = slot.icon;
              return (
                <div key={slot.key} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <SlotIcon size={14} color="#64748b" />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#e2e8f0' }}>
                      {slot.label}
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                      ({slot.hint})
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {Object.entries(SLOT_OPTIONS).map(([optionId, option]) => {
                      // Check permission
                      const requiredPermission = SLOT_PERMISSIONS[optionId];
                      const hasRequiredPermission = !requiredPermission ||
                        isAdmin ||
                        effectiveIsSuperAdmin ||
                        (hasPermission && hasPermission(requiredPermission));

                      if (!hasRequiredPermission) return null;

                      // Check feature flag
                      const requiredFeature = SLOT_FEATURES[optionId];
                      const hasRequiredFeature = !requiredFeature ||
                        effectiveIsSuperAdmin ||
                        tenant?.[requiredFeature];

                      if (!hasRequiredFeature) return null;

                      const isSelected = slotConfig[slot.key] === optionId;
                      // Check if this option is used in another slot
                      const usedInOtherSlot = Object.entries(slotConfig)
                        .some(([key, value]) => key !== slot.key && value === optionId);
                      const Icon = option.icon;

                      return (
                        <button
                          key={optionId}
                          onClick={() => !usedInOtherSlot && handleSlotChange(slot.key, optionId)}
                          disabled={usedInOtherSlot}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: isSelected ? 'rgba(255, 235, 59, 0.15)' : usedInOtherSlot ? '#0f172a' : '#1e293b',
                            border: isSelected ? '2px solid #FFEB3B' : '1px solid #334155',
                            borderRadius: '8px',
                            cursor: usedInOtherSlot ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            color: isSelected ? 'white' : usedInOtherSlot ? '#475569' : '#94a3b8',
                            fontSize: '13px',
                            fontWeight: isSelected ? '600' : '400',
                            opacity: usedInOtherSlot ? 0.5 : 1
                          }}
                          title={usedInOtherSlot ? 'Käytössä toisessa paikassa' : ''}
                        >
                          <Icon size={16} color={isSelected ? '#FFEB3B' : usedInOtherSlot ? '#475569' : '#94a3b8'} />
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Offline Settings Section */}
          <div style={{
            background: '#0f172a',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '1rem'
            }}>
              Offline-tuki
            </h3>

            {/* Offline Toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#1e293b',
              borderRadius: '8px',
              border: '1px solid #334155'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '500',
                  marginBottom: '0.25rem'
                }}>
                  {offlineEnabled ? <Wifi size={18} color="#10b981" /> : <WifiOff size={18} color="#94a3b8" />}
                  Offline-tila
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px'
                }}>
                  {offlineEnabled
                    ? 'Laitteet tallennetaan selaimeen offline-käyttöä varten'
                    : 'Offline-toiminnot pois käytöstä'}
                </div>
              </div>

              {/* Toggle Switch */}
              <button
                onClick={() => handleOfflineToggle(!offlineEnabled)}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  border: 'none',
                  background: offlineEnabled ? '#10b981' : '#334155',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  marginLeft: '1rem'
                }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: offlineEnabled ? '26px' : '2px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>

            {/* Info Box */}
            {offlineEnabled && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                display: 'flex',
                gap: '0.5rem',
                animation: 'fadeIn 0.3s ease-out'
              }}>
                <Clock size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  Laitteiden tiedot (mukaan lukien koulutukset) tallennetaan automaattisesti
                  kun käytät sovellusta verkkoyhteydellä. Voit käyttää näitä tietoja myös ilman verkkoyhteyttä.
                </div>
              </div>
            )}

            {/* Department Pre-caching Section */}
            {offlineEnabled && (
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid #334155',
                animation: 'fadeIn 0.3s ease-out'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Download size={16} />
                  Lataa laitteet välimuistiin
                </h4>

                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  marginBottom: '1rem',
                  lineHeight: '1.5'
                }}>
                  Valitse osastot, joiden laitteet haluat ladata offline-käyttöön:
                </div>

                {/* Department Selection */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginBottom: '1rem',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {departments.map(dept => (
                    <div
                      key={dept.id}
                      onClick={() => toggleDepartment(dept.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        background: selectedDepartments.includes(dept.id) ? 'rgba(255, 235, 59, 0.1)' : '#1e293b',
                        border: `1px solid ${selectedDepartments.includes(dept.id) ? 'rgba(255, 235, 59, 0.3)' : '#334155'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedDepartments.includes(dept.id)) {
                          e.currentTarget.style.background = '#334155';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedDepartments.includes(dept.id)) {
                          e.currentTarget.style.background = '#1e293b';
                        }
                      }}
                    >
                      {selectedDepartments.includes(dept.id) ? (
                        <CheckSquare size={20} color="#FFEB3B" />
                      ) : (
                        <Square size={20} color="#94a3b8" />
                      )}
                      <div style={{
                        color: selectedDepartments.includes(dept.id) ? 'white' : '#94a3b8',
                        fontSize: '14px',
                        fontWeight: selectedDepartments.includes(dept.id) ? '500' : '400'
                      }}>
                        {dept.name}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pre-cache Button */}
                <button
                  onClick={preCacheDevices}
                  disabled={isPrecaching || selectedDepartments.length === 0}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: selectedDepartments.length > 0 ? '#FFEB3B' : '#334155',
                    color: selectedDepartments.length > 0 ? '#0f172a' : '#64748b',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: selectedDepartments.length > 0 && !isPrecaching ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    opacity: isPrecaching ? 0.6 : 1
                  }}
                >
                  {isPrecaching ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #0f172a',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Ladataan... ({precacheProgress.current}/{precacheProgress.total})
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Lataa valitut laitteet ({selectedDepartments.length} osastoa)
                    </>
                  )}
                </button>
              </div>
            )}

          {/* ==================== SUOSTUMUKSET ==================== */}
          <div style={{
            marginTop: '1.5rem',
            borderTop: '1px solid #334155',
            paddingTop: '1.5rem'
          }}>
            <div
              onClick={() => setConsentsExpanded(!consentsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '0.75rem',
                background: '#1e293b',
                borderRadius: consentsExpanded ? '8px 8px 0 0' : '8px',
                border: '1px solid #334155'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Shield size={20} color="#94a3b8" />
                <span style={{ color: 'white', fontWeight: '500' }}>Suostumukset</span>
              </div>
              {consentsExpanded ? (
                <ChevronUp size={20} color="#94a3b8" />
              ) : (
                <ChevronDown size={20} color="#94a3b8" />
              )}
            </div>

            {consentsExpanded && (
              <div style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '1rem'
              }}>
                {loadingConsents ? (
                  <div style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', padding: '1rem' }}>
                    Ladataan...
                  </div>
                ) : userConsents.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', padding: '1rem' }}>
                    Ei tallennettuja suostumuksia
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {userConsents.map((consent, idx) => {
                      const consentInfo = CONSENT_DATA[consent.type] || { name: consent.type, icon: FileText, content: '' };
                      const Icon = consentInfo.icon;
                      const isViewing = viewingConsent === consent.type;

                      return (
                        <div key={idx}>
                          <div
                            onClick={() => setViewingConsent(isViewing ? null : consent.type)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: isViewing ? '#334155' : '#0f172a',
                              borderRadius: isViewing ? '8px 8px 0 0' : '8px',
                              border: '1px solid #334155',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <Icon size={18} color="#22c55e" />
                              <div>
                                <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                                  {consentInfo.name}
                                </div>
                                <div style={{ color: '#64748b', fontSize: '12px' }}>
                                  Versio {consent.version} • Hyväksytty {consent.granted_at ? new Date(consent.granted_at).toLocaleDateString('fi-FI') : '-'}
                                </div>
                              </div>
                            </div>
                            {isViewing ? (
                              <ChevronUp size={18} color="#94a3b8" />
                            ) : (
                              <ChevronDown size={18} color="#94a3b8" />
                            )}
                          </div>

                          {/* Expanded content */}
                          {isViewing && consentInfo.content && (
                            <div style={{
                              padding: '1rem',
                              background: '#0f172a',
                              border: '1px solid #334155',
                              borderTop: 'none',
                              borderRadius: '0 0 8px 8px',
                              maxHeight: '300px',
                              overflow: 'auto'
                            }}>
                              <pre style={{
                                color: '#cbd5e1',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'inherit',
                                margin: 0
                              }}>
                                {consentInfo.content}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -45%);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
}

export default ProfileModal;
